import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import type { User, UserOrganization } from "../../lib/api/users";
import * as userApi from "../../lib/api/users";
import { isCompanySetupComplete } from "../../lib/auth/user-setup-progress";
import type { UserSetupSearch } from "../../lib/auth/user-setup-search";
import { useAuth } from "../../lib/auth/hooks";
import { SetupStepHeader } from "./SetupStepHeader";
import { CreateOrganizationModal } from "../profile/CreateOrganizationModal";
import { OrganizationCard } from "../organizations/OrganizationCard";
import { JoinOrganizationSearch } from "../organizations/JoinOrganizationSearch";
import type { ContextButtonAction as ResourceCardMenuAction } from "../../../ui/components/ListView/components/ContextButton";
import { clearInviteToken } from "../../lib/auth/invite-pending";
import { useClaimPendingOrganizationInvite } from "../../lib/auth/useClaimPendingOrganizationInvite";
import * as confirmStyles from "../organizations/OrganizationDetailPage.css";
import { useI18n } from "../../lib/i18n";

function renderOrganizationCard(
  org: UserOrganization | userApi.OrganizationWithMembership,
  options: {
    isMember?: boolean;
    isPendingRequest?: boolean;
    isPreApproved?: boolean;
    compact?: boolean;
    onRequestMembership?: () => void;
    onInstantJoin?: () => void;
    onCancelMembershipRequest?: () => void;
    menuActions?: ResourceCardMenuAction[];
    menuDisabled?: boolean;
  },
) {
  return (
    <OrganizationCard
      id={org.id}
      name={org.name}
      description={org.description}
      logoUrl={org.logoUrl}
      city={org.city}
      state={org.state}
      roles={org.roleName ? [{ name: org.roleName, color: org.roleColor }] : undefined}
      isMember={options.isMember}
      isPendingRequest={options.isPendingRequest}
      isPreApproved={options.isPreApproved}
      compact={options.compact}
      onRequestMembership={options.onRequestMembership}
      onInstantJoin={options.onInstantJoin}
      onCancelMembershipRequest={options.onCancelMembershipRequest}
      menuActions={options.menuActions}
      menuDisabled={options.menuDisabled}
    />
  );
}

export function OrganizationSetupStep({
  user,
  onPendingChange,
}: {
  user: User | null | undefined;
  search: UserSetupSearch;
  displayEmail: string;
  onPendingChange: () => void;
}) {
  const { accessToken, refreshUser } = useAuth();
  const { t, tx } = useI18n();
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [leaveOrgTarget, setLeaveOrgTarget] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [leaveOrgLoading, setLeaveOrgLoading] = React.useState(false);
  const [leaveOrgError, setLeaveOrgError] = React.useState<string | null>(null);
  const [joinableOrgs, setJoinableOrgs] = React.useState<
    userApi.OrganizationWithMembership[]
  >([]);
  const [joinActionOrgId, setJoinActionOrgId] = React.useState<string | null>(null);

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const complete = isCompanySetupComplete(user);

  const pendingOrganizations = user?.pendingOrganizations ?? [];

  const handleCancelMembership = React.useCallback(
    async (orgId: string) => {
      if (!token) return;
      try {
        await userApi.cancelOrganizationMembershipRequest(token, orgId);
        await refreshUser();
        onPendingChange();
      } catch {
        // ignore
      }
    },
    [token, refreshUser, onPendingChange],
  );

  const reloadJoinableOrgs = React.useCallback(async () => {
    if (!token) {
      setJoinableOrgs([]);
      return;
    }
    try {
      setJoinableOrgs(await userApi.fetchJoinableOrganizations(token));
    } catch {
      setJoinableOrgs([]);
    }
  }, [token]);

  useClaimPendingOrganizationInvite({
    onClaimed: () => {
      void reloadJoinableOrgs();
    },
  });

  React.useEffect(() => {
    void reloadJoinableOrgs();
  }, [reloadJoinableOrgs]);

  const handleInstantJoin = React.useCallback(
    async (orgId: string) => {
      if (!token) return;
      setJoinActionOrgId(orgId);
      try {
        await userApi.instantJoinOrganization(token, orgId);
        clearInviteToken();
        await refreshUser();
        onPendingChange();
        await reloadJoinableOrgs();
      } catch {
        // ignore — user can retry from card
      } finally {
        setJoinActionOrgId(null);
      }
    },
    [token, refreshUser, onPendingChange, reloadJoinableOrgs],
  );

  const handleConfirmLeaveOrganization = React.useCallback(async () => {
    if (!token || !leaveOrgTarget || !user?.id) return;
    setLeaveOrgLoading(true);
    setLeaveOrgError(null);
    try {
      await userApi.leaveOrganization(token, leaveOrgTarget.id, user.id);
      setLeaveOrgTarget(null);
      await refreshUser();
      onPendingChange();
    } catch (e) {
      setLeaveOrgError(
        e instanceof Error ? e.message : t("account.setup.org.errorLeaveFailed"),
      );
    } finally {
      setLeaveOrgLoading(false);
    }
  }, [token, leaveOrgTarget, user?.id, refreshUser, onPendingChange, t]);

  if (complete) {
    const memberships = user?.organizations ?? [];
    const hasMembership = Boolean(user?.hasOrganizationMembership);
    const completeTitle = hasMembership
      ? t("account.setup.org.completeTitleJoined")
      : t("account.setup.org.completeTitlePending");
    const pendingOnly = pendingOrganizations.filter(
      (o) => !memberships.some((m) => m.id === o.id),
    );
    const memberIds = new Set(memberships.map((m) => m.id));
    const joinableOnly = joinableOrgs.filter(
      (o) =>
        o.isPreApproved &&
        !o.isMember &&
        !o.isPendingRequest &&
        !memberIds.has(o.id) &&
        !pendingOnly.some((p) => p.id === o.id),
    );

    return (
      <>
      <div
        role="tabpanel"
        id="user-setup-panel-organization"
        aria-labelledby="user-setup-tab-organization"
        className={authText.setupStepPanelForm}
      >
        <div className={authText.setupStepHeadingGroup}>
          <div
            className={authText.stepIconCircle}
            style={{ background: "#DEF7EC", color: "#057A55", border: "1px solid #BCF0DA" }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
              ✓
            </span>
          </div>
          <SetupStepHeader
            title={completeTitle}
            subtitle={tx("account.setup.org.yourOrganizations")}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
          {joinableOnly.map((org) => (
            <React.Fragment key={`joinable-${org.id}`}>
              {renderOrganizationCard(org, {
                isPreApproved: true,
                compact: true,
                onInstantJoin:
                  joinActionOrgId === org.id
                    ? undefined
                    : () => void handleInstantJoin(org.id),
              })}
            </React.Fragment>
          ))}
          {memberships.map((org) => (
            <React.Fragment key={`member-${org.id}`}>
              {renderOrganizationCard(org, {
                isMember: true,
                compact: true,
                menuActions: [
                  {
                    id: "leave",
                    label: t("account.setup.org.leaveOrg"),
                    destructive: true,
                    onSelect: () =>
                      setLeaveOrgTarget({ id: org.id, name: org.name }),
                  },
                ],
                menuDisabled: leaveOrgLoading,
              })}
            </React.Fragment>
          ))}
          {pendingOnly.map((org) => (
            <React.Fragment key={`pending-${org.id}`}>
              {renderOrganizationCard(org, {
                isPendingRequest: true,
                compact: true,
                onCancelMembershipRequest: () => handleCancelMembership(org.id),
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <Modal
        open={leaveOrgTarget !== null}
        setOpen={(open) => {
          if (!open && !leaveOrgLoading) {
            setLeaveOrgTarget(null);
            setLeaveOrgError(null);
          }
        }}
        minWidth="420px"
        minHeight="auto"
        maxWidth="480px"
      >
        {leaveOrgTarget && (
          <div className={confirmStyles.confirmModalBody}>
            <h3 className={confirmStyles.confirmModalTitle}>
              {tx("account.setup.org.leaveOrgModalTitle")}
            </h3>
            <p className={confirmStyles.confirmModalText}>
              {tx("account.setup.org.leaveOrgConfirmPrefix")}{" "}
              {leaveOrgTarget.name}.
            </p>
            {leaveOrgError && <Callout variant="error">{leaveOrgError}</Callout>}
            <div className={confirmStyles.confirmModalActions}>
              <Button
                color="neutral"
                variant="outline"
                size="md"
                disabled={leaveOrgLoading}
                onClick={() => {
                  setLeaveOrgTarget(null);
                  setLeaveOrgError(null);
                }}
              >
                {tx("shared.common.cancel")}
              </Button>
              <Button
                color="danger"
                variant="solid"
                size="md"
                loading={leaveOrgLoading}
                onClick={() => void handleConfirmLeaveOrganization()}
              >
                {tx("account.setup.org.leaveOrgConfirmButton")}
              </Button>
            </div>
          </div>
        )}
      </Modal>
      </>
    );
  }

  return (
    <>
    <div
      role="tabpanel"
      id="user-setup-panel-organization"
      aria-labelledby="user-setup-tab-organization"
      className={authText.setupStepPanelForm}
    >
      <SetupStepHeader
        title={tx("account.setup.org.title")}
        subtitle={tx("account.setup.org.subtitle")}
      />

      <JoinOrganizationSearch
        user={user}
        onMembershipChange={() => {
          onPendingChange();
          void reloadJoinableOrgs();
        }}
      />

      <Button
        color="neutral"
        variant="outline"
        fullWidth
        onClick={() => setCreateModalOpen(true)}
      >
        {tx("account.setup.org.createNew")}
      </Button>

      <CreateOrganizationModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        onCreated={onPendingChange}
      />
    </div>
    <Modal
      open={leaveOrgTarget !== null}
      setOpen={(open) => {
        if (!open && !leaveOrgLoading) {
          setLeaveOrgTarget(null);
          setLeaveOrgError(null);
        }
      }}
      minWidth="420px"
      minHeight="auto"
      maxWidth="480px"
    >
      {leaveOrgTarget && (
        <div className={confirmStyles.confirmModalBody}>
          <h3 className={confirmStyles.confirmModalTitle}>
            {tx("account.setup.org.leaveOrgModalTitle")}
          </h3>
          <p className={confirmStyles.confirmModalText}>
            {tx("account.setup.org.leaveOrgConfirmPrefix")}{" "}
            {leaveOrgTarget.name}.
          </p>
          {leaveOrgError && <Callout variant="error">{leaveOrgError}</Callout>}
          <div className={confirmStyles.confirmModalActions}>
            <Button
              color="neutral"
              variant="outline"
              size="md"
              disabled={leaveOrgLoading}
              onClick={() => {
                setLeaveOrgTarget(null);
                setLeaveOrgError(null);
              }}
            >
              {tx("shared.common.cancel")}
            </Button>
            <Button
              color="danger"
              variant="solid"
              size="md"
              loading={leaveOrgLoading}
              onClick={() => void handleConfirmLeaveOrganization()}
            >
              {tx("account.setup.org.leaveOrgConfirmButton")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
    </>
  );
}
